import React, { useState, useEffect } from 'react';
import { AZURE } from '../../services/api';
import { Copy, CheckCircle2, AlertCircle, Send, MessageSquare, Maximize2 } from 'lucide-react';
import { COLORS } from '../../components/governance/governanceTheme';
import ArtifactViewer from '../../components/governance/ArtifactViewer';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const BUILDER_LABELS = {
  pt: {
    pageTitle: 'Builder',
    pageSubtitle: 'Gerar tarefas a partir de planos aprovados (Founder-only)',
    chatTitle: 'Chat com Builder',
    chatSubtitle: 'Interface conversacional - Gera summary, tasks, prompts Copilot e identifica research needs',
    you: 'Você', error: 'Erro', builder: 'Builder',
    summaryLabel: '📋 Resumo',
    send: 'Enviar', sending: 'Enviando...',
    selectPlan: '📋 Selecionar Plano',
    refresh: 'Atualizar', refreshing: '⏳',
    noPlans: 'Nenhum plano disponível.',
    noPlansSub: 'Crie um plano no Planner primeiro.',
    planItems: 'items', planHorizon: 'd',
    contextTitle: 'Contexto Builder ↔ Planner',
    contextDesc: 'O Builder usa a fotografia de hoje do Planner para sugerir tarefas sem duplicar o que já está no Canvas.',
    environment: 'Ambiente', generatedAt: 'Gerado em',
    totalItems: 'Total de items', activeTracks: 'Trilhas ativas',
    contextFooter: 'Depois de executar as tasks, use o Planner Canvas para mover os itens entre Now/Next/Later/Done/Blocked e manter a fotografia atualizada.',
    errorTitle: 'Erro', dismiss: '✕',
    selectedPlan: 'Plano Selecionado',
    goal: 'Objetivo:', items: 'Items', horizon: 'Horizonte',
    generateBtn: 'Gerar Tasks do Plano', generating: 'Gerando...',
    tasksTitle: 'Tarefas Geradas',
    noTasks: 'Nenhuma tarefa gerada ainda.',
    noTasksSub: 'Selecione um plano e clique em “Gerar Tasks”.',
    copied: 'Copiado!', copy: 'Copiar',
    copilotPrompt: 'Copilot Prompt',
    dependencies: 'Dependências:',
    placeholder: 'Ex: Quero criar endpoint de ranking e tela de medalhas para natação...',
    taskCount: (n, cost) => `${n} task${n !== 1 ? 's' : ''} • $${cost.toFixed(2)} total`,
    snapshotLabel: (id) => `Snapshot: ${id.substring(0, 8)}...`,
  },
  en: {
    pageTitle: 'Builder',
    pageSubtitle: 'Generate tasks from approved plans (Founder-only)',
    chatTitle: 'Chat with Builder',
    chatSubtitle: 'Conversational interface - Generates summaries, tasks, Copilot prompts and identifies research needs',
    you: 'You', error: 'Error', builder: 'Builder',
    summaryLabel: '📋 Summary',
    send: 'Send', sending: 'Sending...',
    selectPlan: '📋 Select Plan',
    refresh: 'Refresh', refreshing: '⏳',
    noPlans: 'No plans available.',
    noPlansSub: 'Create a plan in Planner first.',
    planItems: 'items', planHorizon: 'd',
    contextTitle: 'Builder ↔ Planner Context',
    contextDesc: "The Builder uses today's Planner snapshot to suggest tasks without duplicating what's already in the Canvas.",
    environment: 'Environment', generatedAt: 'Generated at',
    totalItems: 'Total items', activeTracks: 'Active tracks',
    contextFooter: 'After executing tasks, use the Planner Canvas to move items between Now/Next/Later/Done/Blocked and keep the snapshot updated.',
    errorTitle: 'Error', dismiss: '✕',
    selectedPlan: 'Selected Plan',
    goal: 'Goal:', items: 'Items', horizon: 'Horizon',
    generateBtn: 'Generate Plan Tasks', generating: 'Generating...',
    tasksTitle: 'Generated Tasks',
    noTasks: 'No tasks generated yet.',
    noTasksSub: 'Select a plan and click “Generate Tasks”.',
    copied: 'Copied!', copy: 'Copy',
    copilotPrompt: 'Copilot Prompt',
    dependencies: 'Dependencies:',
    placeholder: 'Ex: I want to create a ranking endpoint and medals screen for swimming...',
    taskCount: (n, cost) => `${n} task${n !== 1 ? 's' : ''} • $${cost.toFixed(2)} total`,
    snapshotLabel: (id) => `Snapshot: ${id.substring(0, 8)}...`,
  },
};

// Chat Interface Component (Builder v2.0 - POST /api/governance/builder/chat)
const BuilderChatBox = ({ onTasksGenerated, t }) => {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversationState, setConversationState] = useState({}); // NEW: Estado conversacional

  const sendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = message.trim();
    setMessage('');
    setLoading(true);
    setError(null);

    // Adiciona mensagem do usuário ao histórico
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      console.log('[Builder V2 DEBUG] 1. Enviando mensagem:', userMessage);
      console.log('[Builder V2 DEBUG] conversation_state atual:', conversationState);
      
      const response = await AZURE.post('/governance/builder/chat', {
        message: userMessage,
        context: {
          ...conversationState  // Passa state da conversa anterior (last_builder_question, current_plan_id)
        }
      });
      
      console.log('[Builder V2 DEBUG] 2. Resposta RAW do axios:', response);
      console.log('[Builder V2 DEBUG] 3. response.data:', response.data);
      
      const { data } = response;
      console.log('[Builder V2 DEBUG] 4. data extraído:', data);
      console.log('[Builder V2 DEBUG] 5. data.success:', data?.success);
      console.log('[Builder V2 DEBUG] 6. data.data:', data?.data);

      if (data.success) {
        const builderResponse = data.data;
        console.log('[Builder V2 DEBUG] 7. builderResponse final:', builderResponse);
        console.log('[Builder V2 DEBUG] 8. builderResponse.reply:', builderResponse?.reply);
        console.log('[Builder V2 DEBUG] 9. builderResponse.text:', builderResponse?.text);
        console.log('[Builder V2 DEBUG] 10. builderResponse.conversation_state:', builderResponse?.conversation_state);
        
        // Atualiza conversation state para próxima mensagem
        if (builderResponse.conversation_state) {
          setConversationState(builderResponse.conversation_state);
          console.log('[Builder V2 DEBUG] conversation_state ATUALIZADO:', builderResponse.conversation_state);
        }
        
        // Adiciona resposta do Builder ao histórico
        setChatHistory(prev => [...prev, { 
          role: 'builder', 
          content: builderResponse
        }]);

        // Se gerou tasks, notifica componente pai
        if (builderResponse.tasks?.length > 0 || builderResponse.tasksForPlanner?.length > 0) {
          onTasksGenerated(builderResponse);
        }
      }
    } catch (err) {
      console.error('[Builder V2 DEBUG] ERRO:', err);
      console.error('[Builder V2 DEBUG] err.response:', err.response);
      console.error('[Builder V2 DEBUG] err.response?.data:', err.response?.data);
      
      // FIX 2026-02-04: Blindagem React Error #31 (nunca renderizar objeto)
      const toErrorString = (error) => {
        if (!error) return "Erro desconhecido";
        if (typeof error === "string") return error;
        
        // axios patterns
        const data = error?.response?.data;
        const msg = data?.error?.message || data?.message || error?.message;
        
        if (typeof msg === "string") return msg;
        
        // last resort: stringify
        try { 
          return JSON.stringify(data || error, null, 2); 
        } catch { 
          return "Erro inesperado (não serializável)"; 
        }
      };
      
      const errorMsg = toErrorString(err);
      console.error('[Builder V2 DEBUG] errorMsg (string):', errorMsg);
      
      setError(errorMsg);
      setChatHistory(prev => [...prev, { 
        role: 'error', 
        content: errorMsg  // Garantido como string
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <MessageSquare className="w-6 h-6" style={{ color: COLORS.primary }} />
        <div>
          <h3 className="text-lg font-bold" style={{ color: COLORS.primary }}>
            {t.chatTitle}
          </h3>
          <p className="text-xs text-gray-500">
            {t.chatSubtitle}
          </p>
        </div>
      </div>

      {/* Chat History */}
      {chatHistory.length > 0 && (
        <div className="mb-4 max-h-96 overflow-y-auto space-y-3 border border-gray-100 rounded-lg p-3">
          {chatHistory.map((msg, idx) => (
            <div key={idx} className={`${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
              {msg.role === 'user' ? (
                <div className="inline-block bg-green-100 text-green-900 px-4 py-2 rounded-lg max-w-lg">
                  <div className="text-xs text-green-600 mb-1">{t.you}</div>
                  <div className="text-sm">{msg.content}</div>
                </div>
              ) : msg.role === 'error' ? (
                <div className="inline-block bg-red-50 text-red-700 px-4 py-2 rounded-lg max-w-lg border border-red-200">
                  <div className="text-xs text-red-500 mb-1">{t.error}</div>
                  <div className="text-sm">{msg.content}</div>
                </div>
              ) : (
                <div className="inline-block bg-gray-50 px-4 py-2 rounded-lg max-w-lg">
                  <div className="text-xs text-gray-500 mb-2">{t.builder}</div>
                  
                  {/* Reply Text (SEMPRE EXIBIR SE EXISTIR) */}
                  {(msg.content.reply || msg.content.text) && (
                    <div className="text-sm text-gray-800 mb-3">
                      {msg.content.reply || msg.content.text}
                    </div>
                  )}
                  
                  {/* Summary */}
                  {msg.content.summary?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs font-semibold text-gray-700 mb-1">{t.summaryLabel}</div>
                      <ul className="text-sm space-y-1">
                        {msg.content.summary.map((line, i) => (
                          <li key={i} className="text-gray-700">• {line}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Tasks Count */}
                  {msg.content.tasks?.length > 0 && (
                    <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded inline-block mb-2">
                      ✅ {msg.content.tasks.length} task(s) detalhada(s) gerada(s)
                    </div>
                  )}

                  {/* Tasks for Planner Count */}
                  {msg.content.tasksForPlanner?.length > 0 && (
                    <div className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded inline-block mb-2 ml-2">
                      🎯 {msg.content.tasksForPlanner.length} task(s) para Planner
                    </div>
                  )}

                  {/* Questions */}
                  {msg.content.questions?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-amber-700 mb-1">❓ Perguntas Bloqueantes</div>
                      <ul className="text-sm space-y-1">
                        {msg.content.questions.map((q, i) => (
                          <li key={i} className="text-amber-700">• {q}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Input Box */}
      <div className="flex gap-2">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t.placeholder}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:opacity-50"
          rows={2}
        />
        <button
          onClick={sendMessage}
          disabled={loading || !message.trim()}
          className="px-6 py-2 bg-gradient-to-r from-[#111111] to-[#374151] text-white rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
        >
          <Send className="w-4 h-4" />
          {loading ? t.sending : t.send}
        </button>
      </div>
    </div>
  );
};

// Plan Selector Component
const PlanSelector = ({ plans, selectedPlanId, onSelect, onRefresh, loading, t }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold" style={{ color: COLORS.primary }}>
          {t.selectPlan}
        </h3>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? t.refreshing : '🔄'} {t.refresh}
        </button>
      </div>

      {plans.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">{t.noPlans}</p>
          <p className="text-xs mt-2">{t.noPlansSub}</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {plans.map(plan => (
            <button
              key={plan.id}
              onClick={() => onSelect(plan.id)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedPlanId === plan.id
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 text-sm">{plan.goal}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Plan ID: {plan.id} • Criado: {new Date(plan.created_at).toLocaleDateString('pt-BR')}
                  </div>
                  {plan.snapshot_id && (
                    <div className="text-xs text-green-600 mt-1 font-mono">
                      Snapshot: {plan.snapshot_id.substring(0, 8)}...
                    </div>
                  )}
                </div>
                {selectedPlanId === plan.id && (
                  <div className="ml-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-3">
                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                  {plan.items_count || 0} items
                </span>
                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                  {plan.horizon_days}d
                </span>
                <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                  {plan.status}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Task Prompt Card Component
const TaskPromptCard = ({ task, onViewArtifact, t }) => {
  const [copied, setCopied] = useState(false);

  const copyPrompt = () => {
    if (task.copilot_prompt) {
      navigator.clipboard.writeText(task.copilot_prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const priorityColors = {
    high: 'border-red-500 bg-red-50',
    medium: 'border-yellow-500 bg-yellow-50',
    low: 'border-green-500 bg-green-50'
  };

  return (
    <div className={`border-l-4 ${priorityColors[task.priority]} rounded-lg p-4 mb-4`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-bold text-gray-900">{task.title}</h4>
          <p className="text-sm text-gray-600 mt-1">{task.description}</p>
        </div>
        <span className="text-xs px-2 py-1 bg-gray-100 rounded font-semibold ml-2">
          {task.priority.toUpperCase()}
        </span>
      </div>

      <div className="text-xs text-gray-500 mb-3">
        ${task.estimated_cost_usd?.toFixed(2)} • 
        Task ID: <span className="font-mono">{task.task_id.substring(0, 8)}...</span>
      </div>

      {/* Copilot Prompt */}
      {task.copilot_prompt && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold" style={{ color: COLORS.primary }}>
              {t.copilotPrompt}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => onViewArtifact && onViewArtifact(task.copilot_prompt, `Prompt: ${task.title}`)}
                className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-xs"
                title="Expandir Prompt"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <button
                onClick={copyPrompt}
                className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-[#111111] to-[#374151] text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-opacity"
              >
                {copied ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    {t.copied}
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    {t.copy}
                  </>
                )}
              </button>
            </div>
          </div>

          <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto max-h-64 whitespace-pre-wrap">
            <code>{task.copilot_prompt}</code>
          </pre>
        </div>
      )}

      {/* Dependencies */}
      {task.dependencies && task.dependencies.length > 0 && (
        <div className="mt-3 text-xs text-gray-600">
          <span className="font-semibold">{t.dependencies}</span> {task.dependencies.join(', ')}
        </div>
      )}
    </div>
  );
};

// Main Component
const GovernanceBuilderPageV2 = () => {
  const { lang } = useGovernanceLang();
  const t = BUILDER_LABELS[lang] || BUILDER_LABELS.pt;
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snapshotId, setSnapshotId] = useState(null);
  const [chatGeneratedTasks, setChatGeneratedTasks] = useState(null); // NEW: Tasks do chat

  // Planner snapshot (fotografia de hoje) para contexto Builder ↔ Planner
  const [plannerSnapshot, setPlannerSnapshot] = useState(null);
  const [plannerSnapshotMeta, setPlannerSnapshotMeta] = useState(null);

  // Artifact Viewer State
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerArtifactId, setViewerArtifactId] = useState(null);
  const [viewerContent, setViewerContent] = useState(null); // For raw content
  const [viewerTitle, setViewerTitle] = useState(null);

  const handleViewArtifact = (contentOrId, title) => {
    // Basic heuristic: check if it looks like an ID
    if (typeof contentOrId === 'string' && (contentOrId.startsWith('art_') || contentOrId.length < 50 && !contentOrId.includes(' '))) {
      setViewerArtifactId(contentOrId);
      setViewerContent(null);
    } else {
      setViewerArtifactId(null);
      setViewerContent(contentOrId);
    }
    setViewerTitle(title);
    setViewerOpen(true);
  };


  useEffect(() => {
    loadPlans();
    loadPlannerSnapshot();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await AZURE.get('/governance/planner/plans');
      if (data.success) {
        setPlans(data.data.plans || []);
        // Auto-select último plano
        if (data.data.plans?.length > 0 && !selectedPlanId) {
          setSelectedPlanId(data.data.plans[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading plans:', error);
      setError('Erro ao carregar planos. Verifique console.');
    } finally {
      setLoading(false);
    }
  };

  const loadPlannerSnapshot = async () => {
    try {
      const { data } = await AZURE.get('/governance/planner/snapshot');
      if (data.success && data.data) {
        const payload = data.data;
        if (payload.snapshot) {
          setPlannerSnapshot(payload.snapshot);
        }
        setPlannerSnapshotMeta({
          environment: payload.environment || 'production',
          generated_at: payload.generated_at || null,
          snapshot_id: payload.snapshot_id || null
        });
      }
    } catch (error) {
      console.error('Error loading planner snapshot:', error);
    }
  };

  // NEW: Callback para quando chat gerar tasks
  const handleChatTasksGenerated = (builderResponse) => {
    setChatGeneratedTasks(builderResponse);
    // Exibe tasks detalhadas na área de tarefas
    if (builderResponse.tasks?.length > 0) {
      const formattedTasks = builderResponse.tasks.map((task, idx) => ({
        task_id: `chat-${Date.now()}-${idx}`,
        title: task.title,
        description: task.why || '',
        priority: 'high',
        copilot_prompt: task.copilotPrompt || '',
        estimated_cost_usd: task.costImpact?.one_time_usd || 0,
        dependencies: task.files || []
      }));
      setTasks(formattedTasks);
    }
  };

  const handleGenerateTasks = async () => {
    if (!selectedPlanId) {
      alert('Selecione um plano primeiro');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data } = await AZURE.post('/governance/builder/tasks', {
        plan_id: selectedPlanId
      });
      
      if (data.success) {
        setTasks(data.data.tasks || []);
        setSnapshotId(data.data.snapshot_id || null);
        console.log(`[Builder] Geradas ${data.data.total_count} tarefas (custo: $${data.data.total_estimated_cost_usd})`);
      }
    } catch (error) {
      console.error('Error generating tasks:', error);
      const errorMsg = error.response?.data?.error?.message || error.message;
      setError(errorMsg);
      
      // Se erro for PLAN_REQUIRED, mostrar mensagem específica
      if (error.response?.data?.error?.code === 'PLAN_REQUIRED') {
        alert('plan_id é obrigatório. Selecione um plano válido.');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === selectedPlanId);

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.bg }}>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-30 mb-6 rounded-xl">
          <div className="flex items-center gap-3 p-4">
            <div className="w-14 h-14 rounded-xl border-2" style={{ borderColor: COLORS.gold }}>
              <div className="w-full h-full bg-gradient-to-br from-[#111111] to-[#374151] rounded-xl flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
            <div>
              <h1 className="text-gray-900 text-3xl font-extrabold">
                {t.pageTitle}
              </h1>
              <p className="text-gray-600">{t.pageSubtitle}</p>
            </div>
          </div>
        </div>

        {/* Contexto Builder ↔ Planner (usa fotografia de hoje do Planner) */}
        {plannerSnapshot && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <h2 className="text-lg font-bold mb-1" style={{ color: COLORS.primary }}>
              {t.contextTitle}
            </h2>
            <p className="text-xs text-gray-600 mb-3">
              {t.contextDesc}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-gray-500">{t.environment}</div>
                <div className="font-semibold">{plannerSnapshotMeta?.environment || 'production'}</div>
              </div>
              {plannerSnapshotMeta?.generated_at && (
                <div>
                  <div className="text-gray-500">{t.generatedAt}</div>
                  <div className="font-semibold">
                    {new Date(plannerSnapshotMeta.generated_at).toLocaleString('pt-BR')}
                  </div>
                </div>
              )}
              <div>
                <div className="text-gray-500">{t.totalItems}</div>
                <div className="font-semibold" style={{ color: COLORS.primary }}>
                  {plannerSnapshot.total_items ?? 0}
                </div>
              </div>
              <div>
                <div className="text-gray-500">{t.activeTracks}</div>
                <div className="font-semibold">
                  {Object.keys(plannerSnapshot.tracks_distribution || {}).length}
                </div>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-gray-500">{t.contextFooter}</p>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-red-700">{t.errorTitle}</div>
              <div className="text-sm text-red-600 mt-1">{error}</div>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600"
            >
              ✕
            </button>
          </div>
        )}

        {/* NEW: Chat Interface (Builder v2.0) */}
        <BuilderChatBox onTasksGenerated={handleChatTasksGenerated} t={t} />

        {/* 2-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Plan Selector */}
          <div className="lg:col-span-1 space-y-6">
            <PlanSelector
              plans={plans}
              selectedPlanId={selectedPlanId}
              onSelect={setSelectedPlanId}
              onRefresh={loadPlans}
              loading={loading}
              t={t}
            />

            {/* Selected Plan Summary */}
            {selectedPlan && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h4 className="font-bold text-gray-900 mb-3">{t.selectedPlan}</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">{t.goal}</span>
                    <div className="font-medium text-gray-900 mt-1">{selectedPlan.goal}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div className="bg-gray-50 rounded p-2">
                      <div className="text-xs text-gray-500">{t.items}</div>
                      <div className="font-bold" style={{ color: COLORS.primary }}>
                        {selectedPlan.items_count || 0}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <div className="text-xs text-gray-500">{t.horizon}</div>
                      <div className="font-bold" style={{ color: COLORS.primary }}>
                        {selectedPlan.horizon_days}d
                      </div>
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerateTasks}
                  disabled={loading}
                  className="w-full mt-4 px-4 py-3 bg-gradient-to-r from-[#111111] to-[#374151] text-white rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {loading ? t.generating : t.generateBtn}
                </button>
              </div>
            )}
          </div>

          {/* Right Column - Generated Tasks */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold" style={{ color: COLORS.primary }}>
                    {t.tasksTitle}
                  </h3>
                  {snapshotId && (
                    <div className="text-xs text-green-600 font-mono mt-1">
                      {t.snapshotLabel(snapshotId)}
                    </div>
                  )}
                </div>
                {tasks.length > 0 && (
                  <div className="text-sm text-gray-600">
                    {t.taskCount(tasks.length, tasks.reduce((sum, tk) => sum + (tk.estimated_cost_usd || 0), 0))}
                  </div>
                )}
              </div>

              {tasks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-sm">{t.noTasks}</p>
                  <p className="text-xs mt-2">{t.noTasksSub}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {tasks.map(task => (
                    <TaskPromptCard key={task.task_id} task={task} onViewArtifact={handleViewArtifact} t={t} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ArtifactViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        artifactId={viewerArtifactId}
        fallbackContent={viewerContent}
        title={viewerTitle}
      />
    </div>
  );
};

export default GovernanceBuilderPageV2;
